/*
Schedule of Classes - React  

Jeroen Soeurt jeroen.soeurt@umgc.edu
-------------------------------------
This was created as a more or less temp solution because the current
SOC from 2006 no longer met the needs of UMGC Europe, and there were
as of Spring 2021 not finalized plans to replace it. The approach is to 
generate one big JSON object containing the schedule that is loaded
up-front, instead of doing AJAX calls to search server-side and
download a sub-set of the schedule to the client. This was done for 
two reasons:
1: 	The server runs an old version of Cold Fusion and there were
  worries about the stability/robustness of it. This approach 
  limits server-side processing, as the query can be cached.
2: 	This approach allows for faster client-side filtering and 
  searching.
3:	This approach does not require parameters to be passed from
  client to server, and thus prevents SQL injection attacks.
Feedback indicated that the load-time of about 7 seconds of the app
was acceptable. If this changes in the future, then an AJAX-based
approach is recommended. In that case, only the loading methods
and filter method in App.js in the client need to be adjusted (and
europapi.cfm of course).

----------------------------------------------------------------
04/27/2021 JS:  Original deployment. Ran into issue where prod servers did not return content-length header.
                Applied temp workaround that assumes a content-length as of today so progress bar works in
                loadClasses().
04/28/2021 JS:  Bug fixes:  
                -Date filter does not include start date due to GMT vs CEST 2 hr time difference.
                -Compare button overlaps notes on mobile on long notes on specific phones.
                -Link on logo or title goes to wrong location
                -Search term disappears after comparing classes.
                Enhancements:
                -Checkbox label indent on second line.
                -Syllabus availability notice if not available
                -Added subject codes
                -Subjects adjust to career
05/03/2021 JS:  -Switched to AJAX per search instead of all data up front. This did not end up
                working. ColdFusion is just too slow.
05/04/2021 JS:  -Optimized by making descriptions a seperate load, and minifying/uglyfying field names
                in JSON. 8.5 mb to 2.9 mb. Stale cache load time from 45s to 11s.
05/06/2021 JS:  -Added "credit hours" after credits indicator.
06/01/2021 JS:  -Added session for staff version
06/16/2021 JS:  -A new delivery method was added without letting me know, and the question was raised why
                it does not work on the SOC. Making changes to facilitate this. The new delivery method
                is coded as location E700. The approach is to deal with it the same was as online classes
                (which are entered as location WEB).
07/19/2021 JS   -Made online the first option. 
                -Changed europeapi.cfm to include a field that shows if it
                should be the current default term. 
                -Changed loadTerms to find the default term and set it
                as termFilter in state. 
                -Updated virtual office link.
07/26/2021 JS:  -Remove virtual as a delivery for which locations are an option in FilterPanel.js.
                -Classes at E994 are now the same as WEB.
                -A social work class is now identified by the career instead of the subject.
*/

import './App.scss';
import Header from './components/Header';
import Footer from './components/Footer';
import FilterPanel from './components/FilterPanel';
import ResultPanel from './components/ResultPanel';
import React, { Component } from 'react';
import LoadingProgressBar from './components/LoadingProgressBar';

export const environment = {
  hostUrl: window.location.hostname
};

class App extends Component {

  // Local development will still have to comment out above and uncomment below
  //default this value in cause env files are jacked
  apiUrl = "https://webappsdev.umgc.edu/soc/europeapi.cfm"

  state = {
    classes: [],
    unfilteredClasses: [],
    searchFilter: "",
    subjects: [],
    terms: [],
    careerFilter: ['UFSU', 'UGRD', 'USAL'],
    instructionModeFilter: ['ONSITE', 'REMOTE', 'FIELDSTUDY', 'VIRTUAL'],
    status: "LOADING",
    termFilter: "",
    locationFilter: [],
    subjectFilter: [],
    locationCheckboxes: [],
    subjectCheckboxes: [],
    ignoreFiltersFilter: false,
    startDateFirstFilter: null,
    startDateSecondFilter: null,
    oneCreditOnly: false,
    area: "EUROPE",
    downrangeLocations: ["E993", "E687", "E983", "E482", "E966", "E967", "E968", "E976", "E402", "E441", "E702", "E974", "E696", "E484"],
    compares: [],
    downloadProgress: 0,
    showClassesStarted: false,
    sessionFilter: "",
    sessions: []
  }

  componentDidMount() {
    this.loadLocations();
  }

  constructor(props) {
    super(props);
    this.clearDates = this.clearDates.bind(this);
    this.addCompare = this.addCompare.bind(this);
    this.showCompares = this.showCompares.bind(this);
    this.showResults = this.showResults.bind(this);
    this.removeCompare = this.removeCompare.bind(this);
    this.checkAllLocations = this.checkAllLocations.bind(this);
    this.checkAllSubjects = this.checkAllSubjects.bind(this);

    //make sure value isn't undefined
    if (process.env.REACT_APP_API_ENDPOINT && process.env.NODE_ENV !== 'production') {
      console.log('setting url');
      console.log(process.env.REACT_APP_API_ENDPOINT);
      this.apiUrl = process.env.REACT_APP_API_ENDPOINT;
    } else {
      this.apiUrl = "//" + environment.hostUrl + "/soc/europeapi.cfm"
    }

  }


  showCompares() {
    this.setState({ status: "SHOW_COMPARES" });
  }

  showResults() {
    if (this.state.classes.length > 0) {
      this.setState({ status: "CLASSES_FOUND" });
    } else {
      this.setState({ status: "NO_CLASSES_FOUND" });
    }
  }

  removeCompare(e) {
    let newCompares = this.state.compares.filter(cls => cls.CLASS_NBR !== parseInt(e.target.id));
    this.setState({ compares: newCompares });
    if (newCompares.length === 0) {
      this.showResults();
    }
  }

  addCompare(e) {
    if (this.state.compares.length < 3) {
      let newCompares = this.state.compares;
      let classToBeAdded = this.state.classes.filter(cls => cls.CLASS_NBR === parseInt(e.target.id));
      newCompares = newCompares.concat(classToBeAdded);
      this.setState({ compares: newCompares });
    }
  }

  loadLocations() {
    fetch(this.apiUrl + "?f=locations")
      .then(res => res.json())
      .then((data) => {
        data.sort((a, b) => (a.COUNTRY > b.COUNTRY) ? 1 : ((b.COUNTRY > a.COUNTRY) ? -1 : 0));
        this.setState({
          locationCheckboxes: data.map((location) => { return { id: location.LOC, value: location.LOC_D, isChecked: true, country: location.COUNTRY, isVisible: !this.state.downrangeLocations.includes(location.LOC) } }),
          locationFilter: data.map((location) => { return location.LOC }),
        }, this.loadSubjects);
      }).catch(console.log);
  }

  loadSubjects() {
    fetch(this.apiUrl + "?f=subjects")
      .then(res => res.json())
      .then((data) => {
        this.setState({
          subjectCheckboxes: data.map((subject) => { return { id: subject.S, value: subject.SD, career: subject.AC, isChecked: true } }),
          subjectFilter: data.map((subject) => { return subject.S }),
        }, this.loadTerms);
      }).catch(console.log);
  }

  loadTerms() {
    fetch(this.apiUrl + "?f=terms")
      .then(res => res.json())
      .then((data) => {
        //Add || data[0].STRM as default to the following in case a DEFAULT_DATE_AFTER not set in Admin for terms being sent.
        this.setState({ terms: data, termFilter: data.find(term => { return term.SELECTED === 1 })?.STRM || data[0].STRM }, this.loadSessions); //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Optional_chaining
      }).catch(console.log);
  }

  loadSessions() {
    fetch(this.apiUrl + "?f=sessions")
      .then(res => res.json())
      .then((data) => {
        this.setState({ sessions: data }, this.loadDescriptions);
      }).catch(console.log);
  }

  async loadDescriptions() {
    this.setState({ loadingText: "Loading course descriptions...." });
    this.setState({ downloadProgress: 0 });

    let xhr = new XMLHttpRequest();
    xhr.open("GET", this.apiUrl + "?f=descriptions");

    xhr.onprogress = event => {
      /*Jeroen Soeurt, 04/27/2021
      Workaround. On 04/27/2021 when first deploying the prod server did not return
      a content length header. The content length as of right now should be 450000,
      so if there is no content-length, assume this is it. This way the progress bar
      works more or less. When the server config is fixed, it will start using the
      real content. In that case remove this workaround during the next deployment. */
      let total = event.total ? event.total : 535000;
      if (this.state.downloadProgress <= ((event.loaded / total) * 100) - 5) {
        this.setState({ downloadProgress: 10 + (event.loaded / total) * 100 });
      }
    };

    xhr.onload = () => {
      if (xhr.status !== 200) {
        console.log(`Error ${xhr.status}: ${xhr.statusText}`);
      } else {
        let response = JSON.parse(xhr.response);
        this.setState({ descriptions: response }, this.loadClasses);
      }
    };

    xhr.send();
  }

  async loadClasses() {
    this.setState({ loadingText: "Loading schedule of classes...." });
    this.setState({ downloadProgress: 0 });

    let xhr = new XMLHttpRequest();
    xhr.open("GET", this.apiUrl + "?f=schedule");

    xhr.onprogress = event => {
      /*Jeroen Soeurt, 04/27/2021
      Workaround. On 04/27/2021 when first deploying the prod server did not return
      a content length header. The content length as of right now should be 8320357,
      so if there is no content-length, assume this is it. This way the progress bar
      works more or less. When the server config is fixed, it will start using the
      real content. In that case remove this workaround during the next deployment. */
      let total = event.total ? event.total : 4000000;
      if (this.state.downloadProgress <= ((event.loaded / total) * 100) - 5) {
        this.setState({ downloadProgress: 10 + (event.loaded / total) * 100 });
      }
    };

    xhr.onload = () => {
      if (xhr.status !== 200) {
        console.log(`Error ${xhr.status}: ${xhr.statusText}`);
      } else {
        let response = JSON.parse(xhr.response);

        this.setState({ unfilteredClasses: response }, this.compileSchedule);
      }
    };

    xhr.send();
  }

  compileSchedule() {
    let newUnfilteredClasses = this.state.unfilteredClasses.map((scheduledClass) => {
      scheduledClass.DESCRIPTION = this.state.descriptions.find((descr) => { return descr.COURSE === (scheduledClass.S + scheduledClass.CN) && descr.STRM === scheduledClass.STRM }).DESCRIPTION;
      return scheduledClass;
    });

    this.setState({ unfilteredClasses: newUnfilteredClasses }, this.updateClasses);
  }

  //Adds an instruction mode to state.instructionMode if it does not already exists.
  addInstructionMode(instructionMode) {
    if (!this.state.instructionModeFilter.includes(instructionMode)) {
      let newInstructionModeFilter = this.state.instructionModeFilter;
      newInstructionModeFilter.push(instructionMode);
      this.setState({ instructionModeFilter: newInstructionModeFilter }, this.updateClasses);
    }
  }

  //Remove an instruction mode from state.instructionMode.
  removeInstructionMode(instructionMode) {
    if (this.state.instructionModeFilter.length > 1) {
      this.setState({ instructionModeFilter: this.state.instructionModeFilter.filter((mode) => { return mode !== instructionMode }) }, this.updateClasses);
    }
  }

  //Adds a credit filter to state.creditFIlter if it does not already exists.
  addCredit(credit) {
    if (!this.state.creditFilter.includes(credit)) {
      let newCreditFilter = this.state.creditFilter;
      newCreditFilter.push(credit);
      this.setState({ creditFilter: newCreditFilter }, this.updateClasses);
    }
  }

  //Remove a credit filter from state.creditFIlter.
  removeCredit(credit) {
    this.setState({ creditFilter: this.state.creditFilter.filter((cr) => { return cr !== credit }) }, this.updateClasses);
  }

  //Clears data limitations.
  clearDates() {
    this.setState({ startDateFirstFilter: null, startDateSecondFilter: null }, this.updateClasses);
  }

  //Set area to Europe or downrange
  setArea(area) {
    if (area === "EUROPE") {
      let newLocationCheckboxes = this.state.locationCheckboxes.map((location) => { return { id: location.id, value: location.value, isChecked: true, country: location.country, isVisible: !this.state.downrangeLocations.includes(location.id) } });
      this.setState({ locationCheckboxes: newLocationCheckboxes }, this.updateClasses);
      this.setState({ area: "EUROPE" });
    } else {
      let newLocationCheckboxes = this.state.locationCheckboxes.map((location) => { return { id: location.id, value: location.value, isChecked: true, country: location.country, isVisible: this.state.downrangeLocations.includes(location.id) } });
      this.setState({ locationCheckboxes: newLocationCheckboxes }, this.updateClasses);
      this.setState({ area: "DOWNRANGE" });
    }
  }

  //Checks or unchecks all locations in the currently select area.
  checkAllLocations(e) {
    let newLocationCheckboxes = this.state.locationCheckboxes.map((location) => {
      if (this.state.area === "EUROPE") {
        return { id: location.id, value: location.value, isChecked: e.target.checked, country: location.country, isVisible: !this.state.downrangeLocations.includes(location.id) }
      } else {
        return { id: location.id, value: location.value, isChecked: e.target.checked, country: location.country, isVisible: this.state.downrangeLocations.includes(location.id) }
      }
    });
    this.setState({ locationCheckboxes: newLocationCheckboxes }, this.updateClasses);
  }

  //Checks or unchecks all subject checkboxes.
  checkAllSubjects(e) {
    let newSubjectCheckboxes = this.state.subjectCheckboxes.map(subject => {
      return { id: subject.id, value: subject.value, career: subject.career, isChecked: e.target.checked }
    });

    this.setState({ subjectCheckboxes: newSubjectCheckboxes }, this.updateClasses);
  }

  //Change handler for filter panel.
  onChange = (e) => {
    if (e.target.id === "chkOnline") {
      e.currentTarget.checked ? this.addInstructionMode('ONLINE') : this.removeInstructionMode('ONLINE');
    }
    else if (e.target.id === "chkOnsite") {
      e.currentTarget.checked ? this.addInstructionMode('ONSITE') : this.removeInstructionMode('ONSITE');
    }
    else if (e.target.id === "chkRemote") {
      e.currentTarget.checked ? this.addInstructionMode('REMOTE') : this.removeInstructionMode('REMOTE');
    }
    else if (e.target.id === "chkVirtual") {
      e.currentTarget.checked ? this.addInstructionMode('VIRTUAL') : this.removeInstructionMode('VIRTUAL');
    }
    else if (e.target.id === "chkFieldStudy") {
      e.currentTarget.checked ? this.addInstructionMode('FIELDSTUDY') : this.removeInstructionMode('FIELDSTUDY');
    }
    else if (e.target.id === "radioUndergrad") {
      this.setState({ sessionFilter: "" }, //Reset session when career changes
        this.setState({ careerFilter: ['UFSU', 'UGRD', 'USAL'] }, this.updateClasses));
    }
    else if (e.target.id === "radioGrad") {
      this.setState({ sessionFilter: "" }, //Reset session when career changes
        this.setState({ careerFilter: ['GBSU', 'GR4T', 'GRAD', 'GSAL'] }, this.updateClasses));
    }
    else if (e.target.id === "dtFirst") {
      this.setState({ startDateFirstFilter: Date.parse(e.target.value) }, this.updateClasses);
    }
    else if (e.target.id === "dtSecond") {
      this.setState({ startDateSecondFilter: Date.parse(e.target.value) }, this.updateClasses);
    }
    else if (e.target.id === "selectTerm") {

      //Uncheck remote if term is greater than Summer 21
      if (parseInt(e.target.value) > 2215) {
        this.removeInstructionMode('REMOTE');
      }
      this.setState({ sessionFilter: "" }, //Reset session when term changes
        this.setState({ termFilter: parseInt(e.target.value) }, this.updateClasses));
    }
    else if (e.target.id === "selectSession") {
      this.setState({ sessionFilter: e.target.value }, this.updateClasses);
    }
    else if (e.target.name === "chkLocation") {
      let newLocationCheckboxes = this.state.locationCheckboxes.map((location) => {
        if (location.id === e.target.id) {
          return { id: location.id, value: location.value, isChecked: !location.isChecked, country: location.country, isVisible: location.isVisible }
        } else {
          return location;
        }
      });
      this.setState({ locationCheckboxes: newLocationCheckboxes }, this.updateClasses);
    }
    else if (e.target.name === "chkSubject") {
      let newSubjectCheckboxes = this.state.subjectCheckboxes.map((subject) => {
        if (subject.id === e.target.id) {
          return { id: subject.id, value: subject.value, career: subject.career, isChecked: !subject.isChecked }
        } else {
          return subject;
        }
      });
      this.setState({ subjectCheckboxes: newSubjectCheckboxes }, this.updateClasses);
    }
    else if (e.target.name === "search") {
      this.setState({ searchFilter: e.target.value.replaceAll(/([\^\$\(\)\*\?\+\|\[\]\.\{\}\=\\])/g, '\\$&') }, this.updateClasses);
    }
    else if (e.target.name === "chkIgnoreFilters") {
      this.setState({ ignoreFiltersFilter: e.target.checked }, this.updateClasses);
    }
    else if (e.target.name === "chkAllLocations") {
      if (e.target.checked) {
        this.setState({ locationFilter: this.state.locationCheckboxes.map((location) => { return location.id }) }, this.updateClasses);
      } else {
        this.setState({ locationFilter: ['WEB'] }, this.updateClasses); //Always keep location web. If the user doens't want online classes then the instruction mode filter will remove it from the results.
      }
    }
    else if (e.target.name === "chkAllSubjects") {
      if (e.target.checked) {
        this.setState({ subjectFilter: this.state.subjectCheckboxes.map((subject) => { return subject.id }) }, this.updateClasses);
      } else {
        this.setState({ subjectFilter: [] }, this.updateClasses);
      }
    }
    else if (e.target.name === "chk1Credit") {
      if (e.target.checked) {
        this.setState({ oneCreditOnly: true }, this.updateClasses);
      } else {
        this.setState({ oneCreditOnly: false }, this.updateClasses);
      }
    }
    else if (e.target.name === "chkClassesStarted") {
      if (e.target.checked) {
        this.setState({ showClassesStarted: true }, this.updateClasses);
      } else {
        this.setState({ showClassesStarted: false }, this.updateClasses);
      }
    }
    //9/28/2023 Check Newly Added Filter
    else if (e.target.name === "chkNewlyAdded") {
      if (e.target.checked) {
        this.setState({ showNewlyAdded: true }, this.updateClasses);
      } else {
        this.setState({ showNewlyAdded: false }, this.updateClasses);
      }
    }
    // 10/19/2023 Intersession Filter
    else if (e.target.name === "chkIntersession") {
      if (e.target.checked) {
        this.setState({ showIntersession: true }, this.updateClasses);
       
        
      } else {
        this.setState({ showIntersession: false }, this.updateClasses);
      }
    }
    else if (e.target.id === "radioEurope") {
      this.setArea("EUROPE");
    }
    else if (e.target.id === "radioDownrange") {
      this.setArea("DOWNRANGE");
    }
    else {
      console.log(e.target.name);
      console.log(e.target.id);
      console.log(e.target.value);
      console.log(e.target.checked);
    }
  }

  //Runs filters on classes and update this in state. React pushes updates
  //to child component automatically. Result panel get classes as prop, so
  //updates by itself.
  //
  //The approach is to first apply filters that remove as many classes as 
  //possible and that are cheap. The more expensive functions will come 
  //later and will have less classes left to filter through.
  updateClasses() {

    this.setState({ 'status': "LOADING" })
    let filteredClasses = this.state.unfilteredClasses;

    //Never show social work classes for downrange for contract compliance reasons. 
    //This even applies when the ignoreFilters button is checked.
    if (this.state.area === "DOWNRANGE") {
      filteredClasses = filteredClasses.filter((cls) => {
        return cls.AC !== "USAL" && cls.AC !== "GSAL";
      });
    }

    if (!this.state.ignoreFiltersFilter) {

      //Term
      filteredClasses = filteredClasses.filter((cls) => {
        // Somewhere the term 2222 is getting converted to a number so convert to string to safeguard. JP 20211013
        // Need to trace back on why when selecting term, 2222 is being converted to a number when it comes over as a string.
        return cls.STRM === this.state.termFilter.toString();
      });

      //Career
      filteredClasses = filteredClasses.filter((cls) => {
        return this.state.careerFilter.includes(cls.AC);
      });

      //Session   
      if (this.state.sessionFilter !== "") {
        filteredClasses = filteredClasses.filter((cls) => {
          return (this.state.sessionFilter.replace(/\s/g, '').split(",").includes(cls.SC)
            &&
            new Date(this.state.sessions.find(s => { return s.SESSION_CODE === this.state.sessionFilter && s.STRM === this.state.termFilter }).SESSION_START_DATE) <= new Date(cls.START_DT)
            &&
            new Date(this.state.sessions.find(s => { return s.SESSION_CODE === this.state.sessionFilter && s.STRM === this.state.termFilter }).SESSION_END_DATE) >= new Date(cls.END_DT)
          );
        });

      }

      //Instruction mode
      filteredClasses = filteredClasses.filter((cls) => {
        return (this.state.instructionModeFilter.includes("ONLINE") && (cls.LOC === "WEB" || cls.LOC === "E994"))
          || (this.state.instructionModeFilter.includes("VIRTUAL") && cls.LOC === "E700")
          || (this.state.instructionModeFilter.includes("FIELDSTUDY") && cls.LOC === "E998")
          || (this.state.instructionModeFilter.includes("ONSITE") && !(cls.LOC === "E701") && !(cls.LOC === "E702") && !(cls.LOC === "WEB") && !(cls.LOC === "E994") && !(cls.LOC === "E998") && !(cls.LOC === "E700"))
          || (this.state.instructionModeFilter.includes("REMOTE") && (cls.LOC === "E701" || cls.LOC === "E702"))
          ;
      });

      //Area
      filteredClasses = filteredClasses.filter((cls) => {
        return ((cls.LOC === "WEB") || (cls.LOC === "E994") || (cls.LOC === "E998") || (cls.LOC === "E700") || (this.state.area === "EUROPE" && !this.state.downrangeLocations.includes(cls.LOC)) || (this.state.area === "DOWNRANGE" && this.state.downrangeLocations.includes(cls.LOC)));
      });

      //Credits
      if (this.state.oneCreditOnly) {
        filteredClasses = filteredClasses.filter((cls) => {
          return cls.CR === 1;
        });
      }

      //Show classes that already started
      if (!(this.state.showClassesStarted)) {
        filteredClasses = filteredClasses.filter((cls) => {
          return Date.parse(cls.START_DT) + 86400000 >= new Date();
        });
      }
       //Show newly added classes 9/27/23 YO
       if (this.state.showNewlyAdded) {
        filteredClasses = filteredClasses.filter((cls) => {
          return cls.NT.length > 1 && cls.NT.includes("Newly Added!");
        });
      }
        //Show Intersession only 10/19/23 YO
        if (this.state.showIntersession) {
          filteredClasses = filteredClasses.filter((cls) => {
             return cls.NT.length > 1 && cls.NT.includes("intersession");
           
          });
        }

      //Start date begin
      if (this.state.startDateFirstFilter) {
        filteredClasses = filteredClasses.filter((cls) => {
          return Date.parse(cls.START_DT) + 86400000 >= this.state.startDateFirstFilter;
        });
      }

      //Start date end 
      if (this.state.startDateSecondFilter) {
        filteredClasses = filteredClasses.filter((cls) => {
          return Date.parse(cls.START_DT) <= this.state.startDateSecondFilter;
        });
      }

      //Location
      filteredClasses = filteredClasses.filter((cls) => {
        return this.state.locationCheckboxes.some(location => location['id'] === cls.LOC && location['isChecked']) || (cls.LOC === "E700") || (cls.LOC === "WEB") || (cls.LOC === "E702") || (cls.LOC === "E701");
      });

      //Subject
      filteredClasses = filteredClasses.filter((cls) => {
        return this.state.subjectCheckboxes.some(subject => subject['id'] === cls.S && subject['isChecked']);
      });
    }

    if (this.state.searchFilter) {
      filteredClasses = filteredClasses.filter((cls) => {
        return (cls.S + cls.CN + "|" + cls.T + "|" + cls.FAC + "|" + cls.SD + "|" + cls.LOC_D + "|" + cls.CS + "|" + cls.CLASS_NBR + "|" + cls.NT).toUpperCase().includes(this.state.searchFilter.toUpperCase())
      });
    }

    if (filteredClasses.length === 0) {
      this.setState({ status: "NO_CLASSES_FOUND" });
    } else {
      this.setState({ status: "CLASSES_FOUND" });
    }

    this.setState({ classes: filteredClasses });
  }

  render() {
    if (this.state.status === "LOADING") {
      return (
        <div>
          <Header />
          <div className="container">
            <LoadingProgressBar downloadProgress={this.state.downloadProgress} loadingText={this.state.loadingText} />
          </div>
          <Footer />
        </div>
      );
    } else {
      return (
        <div>
          <Header />
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-2 d-print-none">
                <FilterPanel
                  locationCheckboxes={this.state.locationCheckboxes}
                  subjectCheckboxes={this.state.subjectCheckboxes}
                  terms={this.state.terms}
                  termFilter={this.state.termFilter}
                  onChange={this.onChange}
                  clearDates={this.clearDates}
                  compares={this.state.compares}
                  showCompares={this.showCompares}
                  status={this.state.status}
                  showResults={this.showResults}
                  checkAllLocations={this.checkAllLocations}
                  checkAllSubjects={this.checkAllSubjects}
                  area={this.state.area}
                  careerFilter={this.state.careerFilter}
                  instructionModeFilter={this.state.instructionModeFilter}
                  creditFilter={this.state.creditFilter}
                  startDateFirstFilter={this.state.startDateFirstFilter ? new Date(this.state.startDateFirstFilter).toISOString().substr(0, 10) : ""}
                  startDateSecondFilter={this.state.startDateSecondFilter ? new Date(this.state.startDateSecondFilter).toISOString().substr(0, 10) : ""}
                  searchFilter={this.state.searchFilter}
                  sessions={this.state.sessions}
                  sessionFilter={this.state.sessionFilter}
                />
              </div>
              <div className="col-lg-10">
                <ResultPanel
                  status={this.state.status}
                  classes={this.state.classes}
                  searchFilter={this.state.searchFilter}
                  addCompare={this.addCompare}
                  compares={this.state.compares}
                  removeCompare={this.removeCompare}
                  downrangeLocations={this.state.downrangeLocations} />
              </div>
            </div>
          </div>
          <Footer />
        </div>
      );
    }
  }
}

export default App;